import { useCallback, useEffect, useState } from 'react';
import { addDays, isPast } from 'date-fns';
import { isEqual, merge } from 'lodash-es';

import {
  ICalendarDayEvent,
  ICalendarOffset,
  IPositionedEvent,
} from '~/Screens/Calendar/interfaces';
import {
  HOURS_IN_DAY,
  MIN_CALENDAR_EVENT_HEIGHT,
  MINUTES_IN_DAY,
  NEW_EVENT_TIME_STEP_IN_MINUTES,
} from '~/Screens/Calendar/constants';
import { TIME_VALUES } from '~/Constants/Date.constants';
import { PLAIN_GREY } from '~/Constants/Colors.constants';
import { useLanguage } from '~/Hooks';
import { EventType } from '~/Models/DTO/Event.model';
import { EventCreationStep } from '~/Screens/Calendar/CalendarView/components/CreatingEvent/types';
import { getEventTimeInSeconds } from '~/Screens/Calendar/CalendarView/components';

interface IUseDropEvent {
  offset: ICalendarOffset;
  startOfPeriod: string;
  calendarDayEvent: ICalendarDayEvent;
}

export const useCreateCalendarEvent = ({
  offset,
  startOfPeriod,
  calendarDayEvent,
}: IUseDropEvent) => {
  const { t } = useLanguage();
  const [creationStep, setCreationStep] = useState<EventCreationStep>(
    EventCreationStep.SELECT_SLOT,
  );
  const [newEvent, setNewEvent] = useState<null | IPositionedEvent>(null);
  const onCancel = useCallback(() => {
    setNewEvent(null);
    setCreationStep(EventCreationStep.SELECT_SLOT);
  }, [setNewEvent, setCreationStep]);

  const calculateNewEventData = useCallback(
    (calendarDayEvent: ICalendarDayEvent) => {
      if (!calendarDayEvent) {
        return null;
      }
      const {
        event: {
          nativeEvent: { offsetY },
        },
        daysOffset,
      } = calendarDayEvent;
      const step = (offset.height * NEW_EVENT_TIME_STEP_IN_MINUTES) / MINUTES_IN_DAY;
      const top = Math.floor(offsetY / step) * step;
      const calendarDay = addDays(new Date(startOfPeriod), daysOffset);
      const start = Number(calendarDay) + Math.round((top / offset.height) * TIME_VALUES.DAY);

      if (isPast(start)) {
        return null;
      }

      return {
        event: {
          color: PLAIN_GREY,
          start: new Date(start).toISOString(),
          end: new Date(start + TIME_VALUES.HOUR).toISOString(),
        },
        position: {
          top,
          left: daysOffset * offset.width,
          width: offset.width,
          height: offset.height / HOURS_IN_DAY,
          daysOffset: 0,
        },
      };
    },
    [setNewEvent, offset, t, startOfPeriod],
  );

  // handle click on calendarDay
  useEffect(() => {
    if (!calendarDayEvent) {
      return;
    }
    if (creationStep === EventCreationStep.SELECT_SLOT) {
      const newEvent = calculateNewEventData(calendarDayEvent);
      if (newEvent) {
        setCreationStep(EventCreationStep.SELECT_EVENT_TYPE);
        setNewEvent(newEvent);
      }
      return;
    }
    if (creationStep === EventCreationStep.SELECT_EVENT_TYPE) {
      setCreationStep(EventCreationStep.SELECT_SLOT);
      setNewEvent(null);
    }
  }, [calendarDayEvent]);

  // handle the newly created event
  useEffect(() => {
    if (creationStep === EventCreationStep.CREATED) {
      setCreationStep(EventCreationStep.SELECT_SLOT);
      setNewEvent(null);
    }
  }, [creationStep]);

  // handle free training
  useEffect(() => {
    if (!newEvent?.event?.type) {
      return;
    }

    const eventData =
      newEvent.event.type === EventType.FREE_TRAINING
        ? merge({}, newEvent, { position: { height: MIN_CALENDAR_EVENT_HEIGHT } })
        : newEvent;
    setNewEvent(eventData);
  }, [newEvent?.event?.type]);

  // handle calendar resize (hide/show list)
  useEffect(() => {
    if (!newEvent || !calendarDayEvent) {
      return;
    }
    setNewEvent((event) =>
      merge({}, event, {
        position: { left: calendarDayEvent.daysOffset * offset.width, width: offset.width },
      }),
    );
  }, [offset.width]);

  // handle time change
  useEffect(() => {
    if (
      !newEvent?.event ||
      newEvent.event.type === EventType.FREE_TRAINING ||
      [newEvent.event.start, newEvent.event.end].some((date) => !date && date === 'Invalid Date')
    ) {
      return;
    }
    const time = getEventTimeInSeconds(newEvent.event);
    const top = ((time.start - time.dayStart) / TIME_VALUES.DAY) * offset.height;
    const height = (time.diff / TIME_VALUES.DAY) * offset.height;
    const updatedEvent = merge({}, newEvent, {
      position: {
        top,
        height: height < MIN_CALENDAR_EVENT_HEIGHT ? MIN_CALENDAR_EVENT_HEIGHT : height,
      },
    });
    if (!isEqual(updatedEvent, newEvent)) {
      setNewEvent(updatedEvent);
    }
  }, [newEvent?.event?.start, newEvent?.event?.end]);

  return { newEvent, setNewEvent, setCreationStep, creationStep, onCancel };
};
